import React, { useState, useEffect, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import MobileSidebar from './MobileSidebar';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { darkMode } = useTheme();
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const location = useLocation();

  // Handle submenu toggles
  const toggleSubmenu = useCallback((menu) => {
    setOpenSubmenu(prevState => prevState === menu ? null : menu);
  }, []);

  // Reset open submenus when location changes
  useEffect(() => {
    setOpenSubmenu(null);
  }, [location.pathname]);

  // Close submenus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-item-dropdown')) {
        setOpenSubmenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Scroll to top on mount and when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const bgColorClass = darkMode ? 'bg-black' : 'bg-white';
  const textColorClass = darkMode ? 'text-white' : 'text-primary';
  const logoSrc = '/assets/logos/SEGWAY.png';

  // Service items with their respective paths - matching mobile sidebar structure
  const serviceItems = [
    { 
      label: 'Mutual Funds', 
      path: '/services/mutual-funds'
    },
    { 
      label: 'Investment Management', 
      path: '/services/investment-management'
    },
    { 
      label: 'Personal Retirement Account', 
      path: '/services/personal-retirement-account'
    },
    { 
      label: 'Securities Trading & Brokerage', 
      path: '/services/securities-trading-brokerage'
    },
    { 
      label: 'Trustee And Registrar Services', 
      path: '/services/trustee-registrar-services'
    },
    { 
      label: 'Wealth Management', 
      path: '/services/wealth-management'
    }
  ];

  // Digital Assets items from v2 header
  const digitalAssetsItems = [
    { 
      label: 'Digital Assets Investment', 
      path: 'https://digitalassets.segwaycapital.com'
    },
  
    { 
      label: 'Futures Trading', 
      path: 'https://digitalassets.segwaycapital.com/futures-trading'
    },
    { 
      label: 'Earn Crypto', 
      path: 'https://digitalassets.segwaycapital.com/earn-crypto'
    },
    { 
      label: 'Tax Reporting', 
      path: 'https://digitalassets.segwaycapital.com/tax-reporting'
    },
    { 
      label: 'Web3', 
      path: 'https://digitalassets.segwaycapital.com/web3'
    },
    { 
      label: 'Buy Crypto', 
      path: 'https://digitalassets.segwaycapital.com/buy-crypto'
    },
    { 
      label: 'Exchange', 
      path: 'https://digitalassets.segwaycapital.com/exchange'
    },
   
  ];

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled 
          ? `${bgColorClass} shadow-md py-2` 
          : `${bgColorClass} py-4`
      }`}
    >
      <div className="container-custom flex justify-between items-center">
        <Link to="/" className="flex items-center" onClick={() => window.scrollTo(0, 0)}>
          <img
            src={logoSrc}
            alt="Segway Capital"
            className="h-12 md:h-16"
          />
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-2">
          <nav className="flex items-center space-x-3">
            <NavLink 
              to="/about" 
              className={({ isActive }) => isActive 
                ? "text-secondary font-semibold py-2 text-sm" 
                : `${textColorClass} hover:text-secondary transition-colors px-2 py-2 font-medium text-sm underline-animation`}
              onClick={() => window.scrollTo(0, 0)}
            >
              About Us
            </NavLink>
            
            {/* Services Dropdown */}
            <div className="nav-item-dropdown relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSubmenu('services');
                }}
                className={`flex items-center ${textColorClass} hover:text-secondary transition-colors px-2 py-2 font-medium text-sm`}
              >
                Services <FaChevronDown className={`ml-1 text-xs transition-transform duration-300 ${openSubmenu === 'services' ? 'rotate-180' : ''}`} />
              </button>
              
              <div className={`absolute left-0 mt-2 w-64 bg-white dark:bg-primary rounded-lg shadow-lg py-2 z-50 transform origin-top transition-all duration-300 ease-in-out ${openSubmenu === 'services' ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'}`}>
                {serviceItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    className="block px-4 py-2 text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => {
                      setOpenSubmenu(null);
                      window.scrollTo(0, 0);
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Digital Assets Dropdown */}
            <div className="nav-item-dropdown relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSubmenu('digital-assets');
                }}
                className={`flex items-center ${textColorClass} hover:text-secondary transition-colors px-2 py-2 font-medium text-sm`}
              >
                <a 
                  href="https://digitalassets.segwaycapital.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-secondary transition-colors"
                >
                  Digital Assets
                </a>
                <FaChevronDown className={`ml-1 text-xs transition-transform duration-300 ${openSubmenu === 'digital-assets' ? 'rotate-180' : ''}`} />
              </button>
              
              <div className={`absolute left-0 mt-2 w-64 bg-white dark:bg-primary rounded-lg shadow-lg py-2 z-50 transform origin-top transition-all duration-300 ease-in-out ${openSubmenu === 'digital-assets' ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'}`}>
                {digitalAssetsItems.map((item, index) => (
                  <a
                    key={index}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2 text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => {
                      setOpenSubmenu(null);
                    }}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>


            
            <NavLink 
              to="/contact" 
              className={({ isActive }) => isActive 
                ? "text-secondary font-semibold py-2 text-sm" 
                : `${textColorClass} hover:text-secondary transition-colors px-2 py-2 font-medium text-sm underline-animation`}
              onClick={() => window.scrollTo(0, 0)}
            >
              Contact Us
            </NavLink>
            
            
            
            <Link 
              to="/apply-now" 
              className="bg-secondary text-black hover:bg-secondary-light px-4 py-2 rounded-lg transition-colors font-medium text-sm"
              onClick={() => window.scrollTo(0, 0)}
            >
              Apply Now
            </Link>
          </nav>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center">
          <button 
            onClick={toggleMenu} 
            className="text-secondary focus:outline-none"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar (Headless UI) */}
      <MobileSidebar open={isOpen} onClose={() => setIsOpen(false)} />
    </header>
  );
};

export default Header; 